/**
 * Supplier Search Types
 * Type definitions for supplier search functionality
 */

import type { Supplier } from '../types';

export enum SupplierSearchStatus {
  Pending = 'pending',
  Processing = 'processing',
  InProgress = 'in_progress',
  Completed = 'completed',
  Failed = 'failed',
}

export interface SupplierSearchTask {
  id: string;
  user_id: string;
  product_id: number;
  external_product_id: string;
  external_task_id: number;
  status: SupplierSearchStatus;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  suppliers_count: number;
}

export interface SupplierSearchState {
  // Map of product_id to search task
  searches: Record<string, SupplierSearchTask>;
  // Map of product_id to polling state
  polling: Record<string, PollingState>;
  // Suppliers management per product
  suppliers: Record<string, {
    list: Supplier[];
    loading: boolean;
    error: string | null;
  }>;
  loading: Record<string, boolean>;
  error: Record<string, string | null>;
}

export interface PollingState {
  active: boolean;
  attempts: number;
  maxAttempts: number;
  interval: number;
  timeoutId?: ReturnType<typeof setTimeout>;
}

export interface StartSearchParams {
  productId: number;
}

export interface CheckSearchStatusParams {
  taskId: string;
  productId: number;
}

export interface SupplierSearchTasksResponse {
  tasks: SupplierSearchTask[];
  total: number;
}

export interface TaskSupplierProduct {
  id: string;
  supplier_id: string;
  product_data: {
    product_id: number;
    product_moq: string;
    product_url: string;
    product_image: string;
    product_price: string;
    product_title: string;
  };
  created_at: string;
}

export interface TaskSupplier {
  id: string;
  task_id: string;
  external_supplier_id: string | null;
  name: string | null;
  address: string | null;
  emails: string[] | null;
  phone: string | null;
  website: string | null;
  founded_year: number | null;
  // Present for manual suppliers (editable via updateManualSupplier)
  supplier_info?: string | null;
  wechat_number?: string | null;
  is_manual: boolean;
  created_at: string;
  products: TaskSupplierProduct[];
}

export interface ProductSuppliersResponse {
  suppliers: TaskSupplier[];
  total: number;
}

